import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase, isSupabaseReady } from '../supabase';
import { initialLeads, initialEvents, initialNotes, initialActivities } from '../utils/data';
import { UsersContext } from './UsersContext';

export const CRMContext = createContext();

// ── helpers ──────────────────────────────────────────────────────────────────

/** Map a Supabase lead row (+ joined phones/emails) → app shape */
const mapLead = (row) => ({
  id: row.id,
  name: row.name,
  company: row.company,
  telegram: row.telegram,
  status: row.status,
  source: row.source,
  referrerType: row.referrer_type,
  referrerManual: row.referrer_manual,
  referrerLeadId: row.referrer_lead_id,
  createdAt: row.created_at,
  phones: (row.lead_phones || []).map((p) => ({ value: p.value, label: p.label })),
  emails: (row.lead_emails || []).map((e) => ({ value: e.value, label: e.label })),
});

const mapEvent = (row) => ({
  id: row.id,
  leadId: row.lead_id,
  type: row.type,
  title: row.title,
  description: row.description,
  date: row.date,
  status: row.status,
  createdAt: row.created_at,
});

const mapNote = (row) => ({
  id: row.id,
  leadId: row.lead_id,
  content: row.content,
  actorId: row.actor_id,
  actorName: row.actor_name,
  createdAt: row.created_at,
});

const mapActivity = (row) => ({
  id: row.id,
  leadId: row.lead_id,
  action: row.action,
  description: row.description,
  actorId: row.actor_id,
  actorName: row.actor_name,
  createdAt: row.created_at,
});

// ── Provider ──────────────────────────────────────────────────────────────────

export const CRMProvider = ({ children }) => {
  const { currentUser } = useContext(UsersContext);

  // ── local state ───────────────────────────────────────────────────────────
  const [leads, setLeads] = useState(
    () => isSupabaseReady ? [] : JSON.parse(localStorage.getItem('crm_leads')) || initialLeads
  );
  const [events, setEvents] = useState(
    () => isSupabaseReady ? [] : JSON.parse(localStorage.getItem('crm_events')) || initialEvents
  );
  const [notes, setNotes] = useState(
    () => isSupabaseReady ? [] : JSON.parse(localStorage.getItem('crm_notes')) || initialNotes
  );
  const [activities, setActivities] = useState(
    () => isSupabaseReady ? [] : JSON.parse(localStorage.getItem('crm_activities')) || initialActivities
  );
  const [loading, setLoading] = useState(isSupabaseReady);

  // ── persist to localStorage (fallback mode only) ──────────────────────────
  useEffect(() => {
    if (isSupabaseReady) return;
    localStorage.setItem('crm_leads', JSON.stringify(leads));
    localStorage.setItem('crm_events', JSON.stringify(events));
    localStorage.setItem('crm_notes', JSON.stringify(notes));
    localStorage.setItem('crm_activities', JSON.stringify(activities));
  }, [leads, events, notes, activities]);

  // ── fetch all CRM data from Supabase ──────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!isSupabaseReady) return;
    setLoading(true);
    try {
      const [leadsRes, eventsRes, notesRes, activitiesRes] = await Promise.all([
        supabase
          .from('leads')
          .select('*, lead_phones(*), lead_emails(*)')
          .order('created_at', { ascending: false }),
        supabase
          .from('events')
          .select('*')
          .order('date', { ascending: true }),
        supabase
          .from('notes')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('activities')
          .select('*')
          .order('created_at', { ascending: false }),
      ]);

      if (leadsRes.data) setLeads(leadsRes.data.map(mapLead));
      if (eventsRes.data) setEvents(eventsRes.data.map(mapEvent));
      if (notesRes.data) setNotes(notesRes.data.map(mapNote));
      if (activitiesRes.data) setActivities(activitiesRes.data.map(mapActivity));
    } catch (err) {
      console.error('[CRMContext] fetchAll error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSupabaseReady && currentUser) fetchAll();
  }, [fetchAll, currentUser]);

  // ── actor helper ──────────────────────────────────────────────────────────
  const actor = () => ({
    actorId: currentUser?.id || null,
    actorName: currentUser?.name || 'System',
  });

  // ── logActivity ───────────────────────────────────────────────────────────
  const logActivity = async (leadId, action, description) => {
    const { actorId, actorName } = actor();
    const entry = {
      leadId,
      action,
      description,
      actorId,
      actorName,
      createdAt: new Date().toISOString(),
    };

    if (!isSupabaseReady) {
      const local = {
        ...entry,
        id: Date.now().toString() + Math.random(),
      };
      setActivities((prev) => [local, ...prev]);
      return;
    }

    const { data, error } = await supabase
      .from('activities')
      .insert({
        lead_id: leadId,
        action,
        description,
        actor_id: actorId,
        actor_name: actorName,
      })
      .select()
      .single();

    if (!error && data) {
      setActivities((prev) => [mapActivity(data), ...prev]);
    }
  };

  // ── addLead ───────────────────────────────────────────────────────────────
  const addLead = async (leadData) => {
    if (!isSupabaseReady) {
      const id = Date.now().toString();
      const newLead = { ...leadData, id, createdAt: new Date().toISOString() };
      setLeads((prev) => [newLead, ...prev]);
      await logActivity(id, 'Lead Created', `Added ${newLead.name} to the system`);
      if (newLead.source === 'Recommended') {
        if (newLead.referrerType === 'manual' && newLead.referrerManual) {
          await logActivity(id, 'Manual Referrer Saved', `Referred by ${newLead.referrerManual}`);
        } else if (newLead.referrerType === 'linked' && newLead.referrerLeadId) {
          const referrer = leads.find((l) => l.id === newLead.referrerLeadId);
          await logActivity(id, 'Linked Referrer Saved', `Referred by ${referrer?.name || 'an existing lead'}`);
          await logActivity(newLead.referrerLeadId, 'Referral Created', `Referred ${newLead.name}`);
        }
      }
      return;
    }

    // Supabase path
    const { phones = [], emails = [], ...rest } = leadData;
    const { data: leadRow, error } = await supabase
      .from('leads')
      .insert({
        name: rest.name,
        company: rest.company || '',
        telegram: rest.telegram || '',
        status: rest.status || 'New',
        source: rest.source || 'Website',
        referrer_type: rest.referrerType || null,
        referrer_manual: rest.referrerManual || '',
        referrer_lead_id: rest.referrerLeadId || null,
      })
      .select()
      .single();

    if (error || !leadRow) {
      console.error('[CRMContext] addLead error:', error);
      return;
    }

    const validPhones = phones.filter((p) => p.value.trim() !== '');
    const validEmails = emails.filter((e) => e.value.trim() !== '');

    if (validPhones.length) {
      await supabase.from('lead_phones').insert(
        validPhones.map((p) => ({ lead_id: leadRow.id, value: p.value, label: p.label }))
      );
    }
    if (validEmails.length) {
      await supabase.from('lead_emails').insert(
        validEmails.map((e) => ({ lead_id: leadRow.id, value: e.value, label: e.label }))
      );
    }

    // Re-fetch the full lead with phones/emails
    const { data: fullLead } = await supabase
      .from('leads')
      .select('*, lead_phones(*), lead_emails(*)')
      .eq('id', leadRow.id)
      .single();

    if (fullLead) setLeads((prev) => [mapLead(fullLead), ...prev]);

    await logActivity(leadRow.id, 'Lead Created', `Added ${leadRow.name} to the system`);

    if (leadRow.source === 'Recommended') {
      if (rest.referrerType === 'manual' && rest.referrerManual) {
        await logActivity(leadRow.id, 'Manual Referrer Saved', `Referred by ${rest.referrerManual}`);
      } else if (rest.referrerType === 'linked' && rest.referrerLeadId) {
        const referrer = leads.find((l) => l.id === rest.referrerLeadId);
        await logActivity(leadRow.id, 'Linked Referrer Saved', `Referred by ${referrer?.name || 'an existing lead'}`);
        await logActivity(rest.referrerLeadId, 'Referral Created', `Referred ${leadRow.name}`);
      }
    }
  };

  // ── updateLead ────────────────────────────────────────────────────────────
  const updateLead = async (id, updates) => {
    const oldLead = leads.find((l) => l.id === id);

    if (!isSupabaseReady) {
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...updates } : l)));
      await logActivity(id, 'Lead Edited', 'Updated lead details');
      if (updates.phones && updates.phones.length > (oldLead?.phones?.length || 0)) {
        await logActivity(id, 'Phone Added', 'Added a new contact number');
      }
      if (updates.emails && updates.emails.length > (oldLead?.emails?.length || 0)) {
        await logActivity(id, 'Email Added', 'Added a new email address');
      }
      if (updates.source && updates.source !== oldLead?.source) {
        await logActivity(id, 'Source Changed', `Source updated to ${updates.source}`);
      }
      if (updates.referrerLeadId && updates.referrerLeadId !== oldLead?.referrerLeadId) {
        const referrer = leads.find((l) => l.id === updates.referrerLeadId);
        await logActivity(id, 'Referral Linked', `Referred by ${referrer?.name}`);
        await logActivity(updates.referrerLeadId, 'Referral Created', `Referred ${oldLead?.name}`);
      }
      return;
    }

    // Supabase path
    const { phones = [], emails = [], ...rest } = updates;

    const { error } = await supabase
      .from('leads')
      .update({
        name: rest.name,
        company: rest.company || '',
        telegram: rest.telegram || '',
        status: rest.status,
        source: rest.source,
        referrer_type: rest.referrerType || null,
        referrer_manual: rest.referrerManual || '',
        referrer_lead_id: rest.referrerLeadId || null,
      })
      .eq('id', id);

    if (error) {
      console.error('[CRMContext] updateLead error:', error);
      return;
    }

    // Replace phones and emails
    const validPhones = phones.filter((p) => p.value.trim() !== '');
    const validEmails = emails.filter((e) => e.value.trim() !== '');

    await supabase.from('lead_phones').delete().eq('lead_id', id);
    await supabase.from('lead_emails').delete().eq('lead_id', id);

    if (validPhones.length) {
      await supabase.from('lead_phones').insert(
        validPhones.map((p) => ({ lead_id: id, value: p.value, label: p.label }))
      );
    }
    if (validEmails.length) {
      await supabase.from('lead_emails').insert(
        validEmails.map((e) => ({ lead_id: id, value: e.value, label: e.label }))
      );
    }

    // Refresh this lead in state
    const { data: fullLead } = await supabase
      .from('leads')
      .select('*, lead_phones(*), lead_emails(*)')
      .eq('id', id)
      .single();

    if (fullLead) {
      setLeads((prev) => prev.map((l) => (l.id === id ? mapLead(fullLead) : l)));
    }

    await logActivity(id, 'Lead Edited', 'Updated lead details');

    if (updates.phones && updates.phones.length > (oldLead?.phones?.length || 0)) {
      await logActivity(id, 'Phone Added', 'Added a new contact number');
    }
    if (updates.emails && updates.emails.length > (oldLead?.emails?.length || 0)) {
      await logActivity(id, 'Email Added', 'Added a new email address');
    }
    if (updates.source && updates.source !== oldLead?.source) {
      await logActivity(id, 'Source Changed', `Source updated to ${updates.source}`);
    }
    if (updates.referrerLeadId && updates.referrerLeadId !== oldLead?.referrerLeadId) {
      const referrer = leads.find((l) => l.id === updates.referrerLeadId);
      await logActivity(id, 'Referral Linked', `Referred by ${referrer?.name}`);
      await logActivity(updates.referrerLeadId, 'Referral Created', `Referred ${oldLead?.name}`);
    }
  };

  // ── addEvent ──────────────────────────────────────────────────────────────
  const addEvent = async (event) => {
    if (!isSupabaseReady) {
      const newEvent = { ...event, id: Date.now().toString(), createdAt: new Date().toISOString() };
      setEvents((prev) => [...prev, newEvent]);
      const typeLabel = newEvent.type === 'task' ? 'Task' : 'Appointment';
      await logActivity(newEvent.leadId, `${typeLabel} Created`, `Created ${newEvent.type}: ${newEvent.title}`);
      return;
    }

    const { data, error } = await supabase
      .from('events')
      .insert({
        lead_id: event.leadId,
        type: event.type,
        title: event.title,
        description: event.description || '',
        date: event.date,
        status: event.status || 'pending',
      })
      .select()
      .single();

    if (error || !data) {
      console.error('[CRMContext] addEvent error:', error);
      return;
    }

    setEvents((prev) => [...prev, mapEvent(data)]);
    const typeLabel = data.type === 'task' ? 'Task' : 'Appointment';
    await logActivity(data.lead_id, `${typeLabel} Created`, `Created ${data.type}: ${data.title}`);
  };

  // ── updateEvent ───────────────────────────────────────────────────────────
  const updateEvent = async (id, updates) => {
    const evt = events.find((e) => e.id === id);

    if (!isSupabaseReady) {
      setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...updates } : e)));
      if (!evt) return;
      const typeLabel = evt.type === 'task' ? 'Task' : 'Appointment';
      if (updates.status === 'completed' && evt.status !== 'completed') {
        await logActivity(evt.leadId, `${typeLabel} Completed`, `Completed ${evt.type}: ${evt.title}`);
      } else if (updates.date && updates.date !== evt.date) {
        await logActivity(evt.leadId, `${typeLabel} Rescheduled`, `Rescheduled ${evt.type}: ${evt.title}`);
      } else {
        await logActivity(evt.leadId, `${typeLabel} Edited`, `Updated ${evt.type}: ${evt.title}`);
      }
      return;
    }

    const { data, error } = await supabase
      .from('events')
      .update({
        type: updates.type,
        title: updates.title,
        description: updates.description || '',
        date: updates.date,
        status: updates.status,
        lead_id: updates.leadId,
      })
      .eq('id', id)
      .select()
      .single();

    if (error || !data) {
      console.error('[CRMContext] updateEvent error:', error);
      return;
    }

    setEvents((prev) => prev.map((e) => (e.id === id ? mapEvent(data) : e)));

    if (!evt) return;
    const typeLabel = evt.type === 'task' ? 'Task' : 'Appointment';
    if (updates.status === 'completed' && evt.status !== 'completed') {
      await logActivity(evt.leadId, `${typeLabel} Completed`, `Completed ${evt.type}: ${evt.title}`);
    } else if (updates.date && updates.date !== evt.date) {
      await logActivity(evt.leadId, `${typeLabel} Rescheduled`, `Rescheduled ${evt.type}: ${evt.title}`);
    } else {
      await logActivity(evt.leadId, `${typeLabel} Edited`, `Updated ${evt.type}: ${evt.title}`);
    }
  };

  // ── addNote ───────────────────────────────────────────────────────────────
  const addNote = async (leadId, content) => {
    const { actorId, actorName } = actor();

    if (!isSupabaseReady) {
      const newNote = {
        id: Date.now().toString(),
        leadId,
        content,
        createdAt: new Date().toISOString(),
        actorId,
        actorName,
      };
      setNotes((prev) => [newNote, ...prev]);
      await logActivity(leadId, 'Note Added', 'Added a note');
      return;
    }

    const { data, error } = await supabase
      .from('notes')
      .insert({
        lead_id: leadId,
        content,
        actor_id: actorId,
        actor_name: actorName,
      })
      .select()
      .single();

    if (error || !data) {
      console.error('[CRMContext] addNote error:', error);
      return;
    }

    setNotes((prev) => [mapNote(data), ...prev]);
    await logActivity(leadId, 'Note Added', 'Added a note');
  };

  // ── getLeadNextFollowUp ───────────────────────────────────────────────────
  const getLeadNextFollowUp = (leadId) => {
    const pendingEvents = events
      .filter((e) => e.leadId === leadId && e.status === 'pending')
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return pendingEvents.length > 0 ? pendingEvents[0].date : null;
  };

  return (
    <CRMContext.Provider
      value={{
        leads,
        events,
        notes,
        activities,
        loading,
        addLead,
        updateLead,
        addEvent,
        updateEvent,
        addNote,
        getLeadNextFollowUp,
        refreshAll: fetchAll,
      }}
    >
      {children}
    </CRMContext.Provider>
  );
};